import { TextField, StandardTextFieldProps, InputAdornment } from "@mui/material";
import React, { useEffect, useState } from "react";

import BNLabelTooltip from "../BNLabelTooptip/BNLabelTooltip";
import { useTheme } from "@mui/material/styles";
import { getNumberFormatGroupSeparator, sxEnsureArray } from "../../../Helpers/helpers";
import { Culture, cultureToRegionId } from "../../../Contract/Culture";
import { regionToPhonePrefix } from "../../../Contract/RegionId";

interface BNInputPropsWithoutFormattedNumber extends StandardTextFieldProps {
  dataUit?: string;
  inputType?: "telephone" | "number" | "string" | "ssn";
  phonePrefix?: string;
  tooltip?: string;
  isTooltipHtml?: boolean;
  isTooltipHelpIcon?: boolean;
  localeFormatting?: Culture;
  tooltipTrackEventName?: string;
  tooltipTrackValue?: string;
  [propName: string]: any;
}

interface BNInputPropsFormattedNumber extends StandardTextFieldProps {
  dataUit?: string;
  inputType?: "formattedNumber";
  phonePrefix?: string;
  tooltip?: string;
  [propName: string]: any;
}

export type BNInputProps = BNInputPropsWithoutFormattedNumber | BNInputPropsFormattedNumber;

const BNInput: React.FC<BNInputProps> = (props) => {
  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    dataUit,
    label,
    helpLabel,
    InputLabelProps,
    inputProps,
    InputProps,
    inputType,
    value,
    tooltip,
    isTooltipHtml,
    isTooltipHelpIcon = false,
    tooltipTrackEventName,
    tooltipTrackValue,
    onChange,
    localeFormatting = "notSet",
    phonePrefix: initialPhonePrefix,
    sx: textFieldPropsSx,
    slotProps,
    ...textFieldProps
  } = props;

  let phonePrefix = initialPhonePrefix;

  const theme = useTheme();

  let fixedLocale = localeFormatting;

  if (localeFormatting === "notSet") {
    fixedLocale = "nb-NO";
  }

  const [formattedValue, setFormattedValue] = useState(value);

  const formatAndSetValue = (value) => {
    if (inputType === "formattedNumber" && !isNaN(parseFloat(value))) {
      const grouping = getNumberFormatGroupSeparator(fixedLocale);
      const regex = new RegExp(`\\${grouping}`, "g");

      const newValue = parseFloat(value.replace(regex, "")).toLocaleString(fixedLocale);
      setFormattedValue(newValue);
      return newValue.replace(regex, "");
    } else if (inputType === "formattedNumber" && value === "") {
      setFormattedValue("");
      return "";
    } else if (inputType === "ssn") {
      return value.toUpperCase();
    }
    return value;
  };

  const handleInputChange = (event) => {
    const formattedValue = formatAndSetValue(event.target.value);
    event.target.value = formattedValue;

    if (onChange) {
      onChange(event);
    }
  };

  useEffect(() => {
    if (inputType === "formattedNumber" && typeof value === "number") {
      formatAndSetValue(value.toString());
    }
  }, [value, inputType]);

  if (inputType === "telephone" && !phonePrefix) {
    phonePrefix = regionToPhonePrefix(cultureToRegionId(fixedLocale));
  }

  const { sx: InputLabelPropsSx, ...restOfInputLabelProps } = { ...slotProps?.inputLabel, ...InputLabelProps };

  return (
    <TextField
      variant="standard"
      label={
        <>
          {label}
          {helpLabel && <i>({helpLabel})</i>}
          {tooltip && (
            <BNLabelTooltip
              tooltip={tooltip}
              setHtml={isTooltipHtml}
              helpIcon={isTooltipHelpIcon}
              trackEventName={tooltipTrackEventName}
              trackValue={tooltipTrackValue}
            />
          )}
        </>
      }
      value={inputType === "formattedNumber" ? formattedValue : value}
      onChange={(event) => {
        handleInputChange(event);
      }}
      fullWidth={props.fullWidth ?? true}
      margin={"normal"}
      type={
        inputType === "telephone" ||
        inputType === "formattedNumber" ||
        inputType === "number" ||
        (inputType === "ssn" &&
          (localeFormatting == "nb-NO" || localeFormatting == "sv-SE" || localeFormatting == "da-DK"))
          ? "tel"
          : "text"
      }
      autoComplete={props.autoComplete || "on"}
      {...textFieldProps}
      sx={[
        {
          "&& .Mui-disabled": {
            "> input": {
              bgcolor: theme.vars.palette.action.disabledBackground,
            },
            "&:hover .MuiInputAdornment-root": {
              borderColor: theme.vars.palette.grey[600],
            },
          },
          "&& .MuiInputBase-formControl": {
            marginTop: theme.spacing(1),
          },
        },
        ...sxEnsureArray(textFieldPropsSx),
      ]}
      slotProps={{
        ...slotProps,
        input:
          inputType !== "telephone"
            ? { ...slotProps?.input, ...InputProps }
            : {
                startAdornment: <InputAdornment position="start">{phonePrefix}</InputAdornment>,
                ...slotProps?.input,
                ...InputProps,
              },

        htmlInput: { "data-uit": props.dataUit, ...slotProps?.htmlInput, ...inputProps },

        inputLabel: {
          shrink: true,
          sx: {
            position: "relative",
            whiteSpace: "normal",
            ...InputLabelPropsSx,
          },
          ...restOfInputLabelProps,
        },
      }}
    />
  );
};
export default BNInput;
